export interface NormalizeClipRangeInput {
  start: number;
  end: number;
  sourceDuration: number;
  minDuration: number;
  maxDuration: number;
}

export interface ClipRange {
  start: number;
  end: number;
  duration: number;
}

export interface BuildCloudflareVideoClipUrlInput {
  endpointOrigin: string;
  sourceUrl: string;
  start: number;
  duration: number;
  width?: number;
}

export interface VideoDurationLimitInput {
  duration?: number | null;
  minDuration: number;
  maxDuration: number;
}

export type VideoDurationLimitError = 'min' | 'max' | null;

const roundToTenth = (value: number) => Math.round(value * 10) / 10;

const formatSeconds = (value: number) =>
  Number.isInteger(value) ? `${value}` : `${roundToTenth(value)}`;

export function getClipDuration(start: number, end: number) {
  return roundToTenth(Math.max(0, end - start));
}

export function normalizeClipRange({
  start,
  end,
  sourceDuration,
  minDuration,
  maxDuration,
}: NormalizeClipRangeInput): ClipRange {
  const safeSourceDuration = Math.max(0, sourceDuration);
  const safeMinDuration = Math.max(0, minDuration);
  const safeMaxDuration = Math.max(safeMinDuration, maxDuration);
  const maxAllowedDuration = Math.min(safeMaxDuration, safeSourceDuration);

  let normalizedStart = Math.min(Math.max(0, start), safeSourceDuration);
  let normalizedEnd = Math.min(Math.max(0, end), safeSourceDuration);

  if (normalizedEnd < normalizedStart) {
    [normalizedStart, normalizedEnd] = [normalizedEnd, normalizedStart];
  }

  let duration = normalizedEnd - normalizedStart;

  if (duration > maxAllowedDuration) {
    normalizedEnd = normalizedStart + maxAllowedDuration;
  }

  if (duration < safeMinDuration) {
    normalizedEnd = normalizedStart + safeMinDuration;
  }

  if (normalizedEnd > safeSourceDuration) {
    normalizedEnd = safeSourceDuration;
    normalizedStart = Math.max(0, normalizedEnd - maxAllowedDuration);
  }

  duration = getClipDuration(normalizedStart, normalizedEnd);

  return {
    start: roundToTenth(normalizedStart),
    end: roundToTenth(normalizedEnd),
    duration,
  };
}

export function buildCloudflareVideoClipUrl({
  endpointOrigin,
  sourceUrl,
  start,
  duration,
  width,
}: BuildCloudflareVideoClipUrlInput) {
  const origin = new URL(endpointOrigin).origin;
  const parsedSourceUrl = new URL(sourceUrl);

  if (!['http:', 'https:'].includes(parsedSourceUrl.protocol)) {
    throw new Error('sourceUrl must be an HTTP URL');
  }

  const options = [
    'mode=video',
    `time=${formatSeconds(start)}s`,
    `duration=${formatSeconds(duration)}s`,
    width ? `width=${Math.round(width)}` : '',
  ]
    .filter(Boolean)
    .join(',');

  return `${origin}/cdn-cgi/media/${options}/${parsedSourceUrl.toString()}`;
}

export function tryBuildCloudflareVideoClipUrl(
  input: BuildCloudflareVideoClipUrlInput
) {
  try {
    return {
      url: buildCloudflareVideoClipUrl(input),
      error: null,
    };
  } catch {
    return {
      url: null,
      error: 'invalid_url',
    };
  }
}

export function getVideoDurationLimitError({
  duration,
  minDuration,
  maxDuration,
}: VideoDurationLimitInput): VideoDurationLimitError {
  if (!duration) return null;
  if (duration < minDuration) return 'min';
  if (duration > maxDuration) return 'max';
  return null;
}
